// Universal printing: renders to a hidden iframe and calls the system print dialog.
// Works on PC & mobile (HP) with ANY printer the OS exposes — USB, network, or Bluetooth
// thermal — because it routes through the native print dialog. Optional Web Bluetooth
// raw ESC/POS path is provided for direct thermal printers on Chrome/Android.

const idr = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

import JsBarcode from "jsbarcode";
import { RECEIPT_LOGO } from "./receiptLogo";
const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function barcodeDataURL(code, { width = 1.8, height = 70 } = {}) {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(code || "0"), { format: "CODE128", displayValue: false, width, height, margin: 0 });
    return canvas.toDataURL("image/png");
  } catch (e) { return ""; }
}

const FONT_URL = "https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap";
const PX_TO_MM = 25.4 / 96;
export const isAndroid = () => /android/i.test(navigator.userAgent);

const PC_FONT = "'Courier New',ui-monospace,monospace";
const WEB_FONT = "'Courier Prime','Courier New',monospace";

// Shared receipt styles; `p` is the root selector (iframe body on PC, in-page root on Android).
function receiptCSS(p, widthMm, font = WEB_FONT) {
  return `${p}{margin:0;padding:3mm;width:${widthMm}mm;font-family:${font};font-size:12px;
      color:#000;background:#fff;line-height:1.35;-webkit-text-size-adjust:100%;text-size-adjust:100%;text-align:left}
    ${p} *{box-sizing:border-box;font-family:inherit}
    ${p} img{max-width:100%;height:auto}
    ${p} .c{text-align:center}${p} .b{font-weight:700}${p} .lg{font-size:15px}
    ${p} .r{display:flex;justify-content:space-between;gap:6px}
    ${p} .dash{border-top:1px dashed #000;margin:5px 0}
    ${p} .barcode{display:flex;align-items:flex-end;gap:1px;height:44px;justify-content:center;margin:4px 0}
    ${p} .barcode span{background:#000;height:100%}`;
}

const pageCSS = (widthMm, heightPx) => `@page{size:${widthMm}mm ${Math.ceil(heightPx * PX_TO_MM) + 4}mm;margin:0}`;

async function settle(doc) {
  const imgs = [...doc.querySelectorAll("img")].filter((i) => !i.complete);
  await Promise.race([
    Promise.all([doc.fonts?.ready, ...imgs.map((i) => new Promise((r) => { i.onload = i.onerror = r; }))]),
    new Promise((r) => setTimeout(r, 1500)),
  ]);
}

// PC: unchanged proven path — hidden iframe, @page height auto (paper as long as the content).
function printInFrame(inner, widthMm, onOpen) {
  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><style>@page{size:${widthMm}mm auto;margin:0}
    ${receiptCSS("body", widthMm, PC_FONT)}</style></head><body>${inner}</body></html>`);
  doc.close();
  const w = iframe.contentWindow;
  w.addEventListener("beforeprint", onOpen, { once: true });
  w.focus();
  setTimeout(() => { try { w.print(); } catch (e) {} setTimeout(() => iframe.remove(), 1500); }, 350);
}

// Android Chrome prints the parent page instead of an iframe, so print an isolated in-page root.
function printInPage(inner, widthMm) {
  document.getElementById("jjl-print-root")?.remove();
  document.getElementById("jjl-print-style")?.remove();
  const root = document.createElement("div");
  root.id = "jjl-print-root";
  root.innerHTML = inner;
  const style = document.createElement("style");
  style.id = "jjl-print-style";
  const base = `${receiptCSS("#jjl-print-root", widthMm)}
    @media screen{#jjl-print-root{position:fixed;left:-10000px;top:0}}
    @media print{html,body{margin:0!important;padding:0!important;background:#fff!important;width:${widthMm}mm!important;min-height:0!important}
      body>*:not(#jjl-print-root){display:none!important}#jjl-print-root{position:static}}`;
  style.textContent = base;
  document.head.appendChild(style);
  document.body.appendChild(root);
  if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
    const l = document.createElement("link"); l.rel = "stylesheet"; l.href = FONT_URL; document.head.appendChild(l);
  }
  setTimeout(async () => {
    await settle(document);
    style.textContent = `${base}\n${pageCSS(widthMm, root.scrollHeight)}`;
    try { window.print(); } catch (e) {}
  }, 150);
}

// Resolves true if the system print dialog actually opened within ~2.5s.
export function printHTML(inner, { paper = "80" } = {}) {
  const widthMm = paper === "58" ? 58 : 80;
  return new Promise((resolve) => {
    let opened = false;
    const mark = () => { opened = true; };
    window.addEventListener("beforeprint", mark, { once: true });
    if (isAndroid()) printInPage(inner, widthMm); else printInFrame(inner, widthMm, mark);
    setTimeout(() => { window.removeEventListener("beforeprint", mark); resolve(opened); }, 2500);
  });
}

export function receiptHTML(t) {
  const items = t.items.map((i) => {
    const disc = i.orig_price && i.orig_price > i.price;
    return `<div class="r"><span>${i.qty}x ${i.name}</span><span>${idr(i.price * i.qty)}</span></div>` +
    (disc ? `<div class="r" style="font-size:10px"><span style="text-decoration:line-through">@${idr(i.orig_price)} (diskon)</span><span style="text-decoration:line-through">${idr(i.orig_price * i.qty)}</span></div>` : "") +
    (i.note ? `<div style="font-size:10px;font-style:italic">  ${i.note}</div>` : "");
  }).join("");
  return `
  <div class="c"><img src="${RECEIPT_LOGO}" style="width:70%;max-width:200px;margin:0 auto 3px;display:block"/></div>
  <div class="c">${t.branch_name || ""}</div>
  <div class="dash"></div>
  <div class="r"><span>No ${t.receipt_no}</span><span>${t.mode === "dinein" ? "Meja " + t.table : "Self"}</span></div>
  <div class="r"><span>Kasir ${t.cashier_name}</span></div>
  <div class="r"><span>${new Date(t.created_at).toLocaleString("id-ID")}</span></div>
  <div class="dash"></div>${items}<div class="dash"></div>
  <div class="r"><span>Subtotal</span><span>${idr(t.subtotal)}</span></div>
  ${t.special_discount > 0 ? `<div class="r"><span>Diskon Khusus${t.special_label ? " (" + t.special_label + ")" : ""}</span><span>-${idr(t.special_discount)}</span></div>` : ""}
  ${t.discount > 0 ? `<div class="r"><span>Diskon</span><span>-${idr(t.discount)}</span></div>` : ""}
  <div class="r"><span>Service ${t.service_rate}%</span><span>${idr(t.service)}</span></div>
  <div class="r"><span>PB1 ${t.tax_rate}%</span><span>${idr(t.tax)}</span></div>
  <div class="dash"></div>
  <div class="r b lg"><span>TOTAL</span><span>${idr(t.total)}</span></div>
  <div class="r"><span>${(t.payment_method || "").toUpperCase()}</span><span>${t.payment_method === "cash" ? idr(t.cash_received) : (t.trace_number || "-")}</span></div>
  ${t.payment_method === "cash" ? `<div class="r"><span>Kembali</span><span>${idr(t.change)}</span></div>` : ""}
  <div class="dash"></div>
  <div class="c">Terima kasih & selamat menikmati! 🔥</div>
  ${t.receipt_footer ? `<div class="dash"></div><div class="c" style="font-size:10px;white-space:pre-wrap">${esc(t.receipt_footer)}</div>` : ""}`;
}

function barcodeBars(code) {
  return String(code || "0").split("").flatMap((d) => { const n = parseInt(d, 10) + 1; return [n, 3 - (n % 3) + 1]; })
    .map((w) => `<span style="width:${w}px"></span>`).join("");
}

export function stickerHTML(p, size = "3x2") {
  const dim = size === "3x2" ? { w: 30, h: 20 } : { w: 40, h: 20 };
  return `<div style="width:${dim.w}mm;height:${dim.h}mm;padding:1mm;text-align:center">
    <div class="b" style="font-size:11px;white-space:nowrap;overflow:hidden">${p.name}</div>
    <div style="font-size:8px">Kemas: ${new Date().toLocaleDateString("id-ID")}</div>
    <div class="b" style="font-size:10px">${idr(p.price)}</div>
    <div class="barcode" style="height:26px">${barcodeBars(p.barcode)}</div>
  </div>`;
}

export function billHTML(t) {
  const items = t.items.map((i) => {
    const disc = i.orig_price && i.orig_price > i.price;
    return `<div class="r"><span>${i.qty}x ${i.name}</span><span>${idr(i.price * i.qty)}</span></div>` +
    (disc ? `<div class="r" style="font-size:10px"><span style="text-decoration:line-through">@${idr(i.orig_price)} (diskon)</span><span style="text-decoration:line-through">${idr(i.orig_price * i.qty)}</span></div>` : "") +
    (i.note ? `<div style="font-size:10px;font-style:italic">  ${i.note}</div>` : "");
  }).join("");
  return `
  <div class="c"><img src="${RECEIPT_LOGO}" style="width:70%;max-width:200px;margin:0 auto 3px;display:block"/></div>
  <div class="c">${t.branch_name || ""}</div>
  <div class="c b">** TAGIHAN SEMENTARA **</div>
  <div class="c" style="font-size:10px">Belum dibayar</div>
  <div class="dash"></div>
  <div class="r"><span>${t.table ? "Meja " + t.table : "Dine-In"}</span><span>${new Date().toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span></div>
  <div class="dash"></div>${items}<div class="dash"></div>
  <div class="r"><span>Subtotal</span><span>${idr(t.subtotal)}</span></div>
  ${t.special_discount > 0 ? `<div class="r"><span>Diskon Khusus${t.special_label ? " (" + t.special_label + ")" : ""}</span><span>-${idr(t.special_discount)}</span></div>` : ""}
  ${t.service_rate > 0 ? `<div class="r"><span>Service ${t.service_rate}%</span><span>${idr(t.service)}</span></div>` : ""}
  <div class="r"><span>PB1 ${t.tax_rate}%</span><span>${idr(t.tax)}</span></div>
  <div class="dash"></div>
  <div class="r b lg"><span>TOTAL</span><span>${idr(t.total)}</span></div>
  <div class="dash"></div>
  <div class="c" style="font-size:10px">Silakan lakukan pembayaran di kasir.<br/>Terima kasih 🔥</div>`;
}

export function testHTML() {
  return `<div class="c b lg">DA LE TANG HuoGuo</div><div class="c">TEST PRINT</div><div class="dash"></div>
  <div class="r"><span>Printer</span><span>OK</span></div>
  <div class="r"><span>Kertas</span><span>OK</span></div>
  <div class="barcode">${barcodeBars("12345678")}</div>
  <div class="dash"></div><div class="c">Koneksi printer berhasil ✔</div>`;
}

// ---- Optional: direct Web Bluetooth ESC/POS ----
let _btChar = null;
let _btName = null;
const SVC = "000018f0-0000-1000-8000-00805f9b34fb";

export async function connectBluetooth() {
  if (!navigator.bluetooth) throw new Error("Perangkat/browser ini tidak mendukung Web Bluetooth. Gunakan Chrome di Android/PC.");
  const device = await navigator.bluetooth.requestDevice({ filters: [{ services: [SVC] }], optionalServices: [SVC] });
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(SVC);
  const chars = await service.getCharacteristics();
  _btChar = chars.find((c) => c.properties.write || c.properties.writeWithoutResponse) || chars[0];
  _btName = device.name || "Bluetooth Printer";
  device.addEventListener("gattserverdisconnected", () => { _btChar = null; });
  return _btName;
}
export const bluetoothName = () => _btName;
export const bluetoothConnected = () => !!_btChar;

export async function btPrintText(text, { autoCut = true } = {}) {
  if (!_btChar) throw new Error("Printer Bluetooth belum terhubung.");
  const cut = autoCut ? "\x1D\x56\x42\x00" : "";  // GS V 66 0 = partial auto-cut
  const data = new TextEncoder().encode("\x1B\x40" + text + "\n\n\n\n" + cut);
  await btWrite(data);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Small paced writes so cheap printers' buffers never overflow (overflow = garbage characters).
async function btWrite(data, pause = 0) {
  const withResp = _btChar.properties.write;
  for (let i = 0; i < data.length; i += 100) {
    const chunk = data.slice(i, i + 100);
    if (withResp) await _btChar.writeValue(chunk);
    else { await _btChar.writeValueWithoutResponse(chunk); await sleep(25); }
    if (pause) await sleep(pause);
  }
}

// Paints the browser's own layout of `root` onto a canvas (exact positions, same fonts).
function paintLayout(ctx, root, s) {
  const base = root.getBoundingClientRect();
  const X = (v) => (v - base.left) * s, Y = (v) => (v - base.top) * s;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType === 1) {
      const cs = getComputedStyle(n), r = n.getBoundingClientRect();
      const bg = cs.backgroundColor;
      if (bg && !/rgba\(0, 0, 0, 0\)|transparent|rgb\(255, 255, 255\)/.test(bg)) { ctx.fillStyle = "#000"; ctx.fillRect(X(r.left), Y(r.top), r.width * s, r.height * s); }
      if (n.tagName === "IMG" && n.complete) ctx.drawImage(n, X(r.left), Y(r.top), r.width * s, r.height * s);
      const bw = parseFloat(cs.borderTopWidth);
      if (cs.borderTopStyle !== "none" && bw > 0) {
        ctx.save(); ctx.strokeStyle = "#000"; ctx.lineWidth = Math.max(1, bw * s);
        if (cs.borderTopStyle === "dashed") ctx.setLineDash([3 * s, 2 * s]);
        const y = Y(r.top) + ctx.lineWidth / 2;
        ctx.beginPath(); ctx.moveTo(X(r.left), y); ctx.lineTo(X(r.right), y); ctx.stroke(); ctx.restore();
      }
      continue;
    }
    const el = n.parentElement, cs = getComputedStyle(el);
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${parseFloat(cs.fontSize) * s}px ${cs.fontFamily}`;
    ctx.fillStyle = "#000"; ctx.textBaseline = "middle";
    const strike = cs.textDecorationLine.includes("line-through");
    const range = document.createRange();
    for (const m of n.data.matchAll(/\S+/g)) {
      range.setStart(n, m.index); range.setEnd(n, m.index + m[0].length);
      const r = range.getClientRects()[0];
      if (!r) continue;
      const cy = Y(r.top + r.height / 2);
      ctx.fillText(m[0], X(r.left), cy, r.width * s + 2);
      if (strike) ctx.fillRect(X(r.left), cy, r.width * s, Math.max(1, s));
    }
  }
}

// Renders the same HTML used for PC printing into a bitmap so Bluetooth output matches exactly.
async function renderBitmap(inner, widthMm) {
  const dots = widthMm === 58 ? 384 : 576;
  const root = document.createElement("div");
  root.id = "jjl-bt-root";
  root.innerHTML = inner;
  const style = document.createElement("style");
  style.textContent = `${receiptCSS("#jjl-bt-root", widthMm)}#jjl-bt-root{position:fixed;left:-10000px;top:0}`;
  document.head.appendChild(style);
  document.body.appendChild(root);
  if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
    const l = document.createElement("link"); l.rel = "stylesheet"; l.href = FONT_URL; document.head.appendChild(l);
  }
  try {
    await settle(document);
    const r = root.getBoundingClientRect();
    const s = dots / r.width;
    const out = document.createElement("canvas");
    out.width = dots; out.height = Math.ceil(r.height * s);
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, out.width, out.height);
    paintLayout(ctx, root, s);
    return out;
  } finally { root.remove(); style.remove(); }
}

function rasterBytes(canvas) {
  const { width: w, height: h } = canvas;
  const px = canvas.getContext("2d").getImageData(0, 0, w, h).data;
  const bw = w / 8, band = 24, parts = [];
  for (let y0 = 0; y0 < h; y0 += band) {
    const rows = Math.min(band, h - y0);
    const buf = new Uint8Array(8 + bw * rows);
    buf.set([0x1d, 0x76, 0x30, 0, bw & 255, bw >> 8, rows & 255, rows >> 8]);
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < w; x++) {
        const i = ((y0 + y) * w + x) * 4;
        if (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114 < 160) buf[8 + y * bw + (x >> 3)] |= 0x80 >> (x & 7);
      }
    }
    parts.push(buf);
  }
  return parts;
}

// Text fallback built from the same HTML: centered lines, left/right rows, dashed separators.
function htmlToText(inner, cols) {
  const root = document.createElement("div");
  root.innerHTML = inner;
  const clip = (s) => s.replace(/\s+/g, " ").trim().replace(/[^\x20-\x7E]/g, "");
  const center = (s) => s.length >= cols ? s : " ".repeat(Math.floor((cols - s.length) / 2)) + s;
  const wrap = (s) => { const out = []; let line = ""; s.split(" ").forEach((w) => { if ((line + " " + w).trim().length > cols) { out.push(line); line = w; } else line = (line + " " + w).trim(); }); if (line) out.push(line); return out; };
  const lines = [];
  [...root.children].forEach((el) => {
    if (el.classList.contains("dash")) { lines.push("-".repeat(cols)); return; }
    if (el.querySelector("img")) { lines.push(center("DA LE TANG HuoGuo")); return; }
    if (el.classList.contains("r")) {
      const [a, b] = [...el.children].map((c) => clip(c.textContent));
      const right = b || "";
      if (a.length + right.length + 1 <= cols) lines.push(a + " ".repeat(cols - a.length - right.length) + right);
      else { wrap(a).forEach((l) => lines.push(l)); if (right) lines.push(" ".repeat(cols - right.length) + right); }
      return;
    }
    const t = clip(el.innerText || el.textContent);
    if (!t) return;
    wrap(t).forEach((l) => lines.push(el.classList.contains("c") ? center(l) : l));
  });
  return lines.join("\n") + "\n";
}

export async function btPrintHTML(inner, { paper = "80", autoCut = true } = {}) {
  if (!_btChar) throw new Error("Printer Bluetooth belum terhubung.");
  const tail = new Uint8Array([0x1b, 0x64, 4, ...(autoCut ? [0x1d, 0x56, 0x42, 0x00] : [])]);
  await btWrite(new Uint8Array([0x1b, 0x40]));
  if (localStorage.getItem("jjl_bt_mode") === "text") {
    await btWrite(new TextEncoder().encode(htmlToText(inner, paper === "58" ? 32 : 48)));
  } else {
    const canvas = await renderBitmap(inner, paper === "58" ? 58 : 80);
    for (const band of rasterBytes(canvas)) { await btWrite(band); await sleep(40); }
  }
  await btWrite(tail);
}

// Prints via Bluetooth when a printer is connected, otherwise via the system print dialog.
export async function printAny(inner, { paper = "80" } = {}) {
  if (!_btChar) return printHTML(inner, { paper });
  try { await btPrintHTML(inner, { paper, autoCut: localStorage.getItem("jjl_autocut") !== "0" }); return true; }
  catch (e) { return false; }
}

export function receiptText(t) {
  const idr = (n) => "Rp" + (Number(n) || 0).toLocaleString("id-ID");
  const L = "--------------------------------";
  let s = "     DA LE TANG HuoGuo\n" + (t.branch_name || "") + "\n" + L + "\n";
  s += "No " + t.receipt_no + "\nKasir " + t.cashier_name + "\n" + L + "\n";
  t.items.forEach((i) => { s += `${i.qty}x ${i.name}\n`; if (i.orig_price && i.orig_price > i.price) s += `   Normal ${idr(i.orig_price)} (diskon)\n`; s += `   ${idr(i.price * i.qty)}\n`; });
  s += L + "\nSubtotal " + idr(t.subtotal) + "\n";
  if (t.special_discount > 0) s += "Diskon Khusus -" + idr(t.special_discount) + "\n";
  if (t.discount > 0) s += "Diskon -" + idr(t.discount) + "\n";
  s += `Service ${t.service_rate}% ` + idr(t.service) + "\n" + `PB1 ${t.tax_rate}% ` + idr(t.tax) + "\n" + L + "\n";
  s += "TOTAL " + idr(t.total) + "\n";
  s += (t.payment_method || "").toUpperCase() + " " + (t.payment_method === "cash" ? idr(t.cash_received) : (t.trace_number || "-")) + "\n";
  if (t.payment_method === "cash") s += "Kembali " + idr(t.change) + "\n";
  let out = s + L + "\n   Terima kasih!\n";
  if (t.receipt_footer) out += t.receipt_footer + "\n";
  return out;
}

export function printSticker(p, size = "3x2") {
  const dim = size === "3x2" ? { w: 30, h: 20 } : { w: 40, h: 20 };
  const bcW = dim.w - 5; // barcode width in mm (e.g. 35mm on a 40mm label)
  const bc = barcodeDataURL(p.barcode, { width: 3, height: 140 });
  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><style>
    @page{size:${dim.w}mm ${dim.h}mm;margin:0}
    html,body{margin:0;padding:0}
    .lbl{width:${dim.w}mm;height:${dim.h}mm;padding:.6mm;box-sizing:border-box;text-align:center;font-family:Arial,sans-serif;color:#000;display:flex;flex-direction:column;justify-content:center;align-items:center}
    .nm{font-weight:800;font-size:8pt;line-height:1;max-height:2em;overflow:hidden}
    .pr{font-weight:800;font-size:8pt;line-height:1.1}.dt{font-size:5pt;line-height:1}
    .bc{height:11mm;width:${bcW}mm;object-fit:fill;margin-top:.4mm;image-rendering:crisp-edges}
  </style></head><body><div class="lbl">
    <div class="nm">${p.name}</div>
    <div class="pr">Rp ${Number(p.price).toLocaleString("id-ID")}</div>
    <div class="dt">${new Date().toLocaleDateString("id-ID")}</div>
    <img class="bc" src="${bc}" alt="" />
  </div></body></html>`);
  doc.close();
  const w = iframe.contentWindow; w.focus();
  setTimeout(() => { try { w.print(); } catch (e) {} setTimeout(() => iframe.remove(), 1500); }, 350);
}
